import type { ApolloError, OperationVariables, QueryHookOptions, QueryResult } from '@apollo/client';

import { useEffect } from 'react';

export type UseUserQueryOptions<TData extends { currentUser?: any }, TVariables extends OperationVariables> = {
  queryHook: (options: QueryHookOptions<TData, TVariables>) => QueryResult<TData, TVariables>;
  isAuthenticated: () => boolean;
  signOut: () => void;
  queryOptions?: Omit<QueryHookOptions<TData, TVariables>, 'skip'>;
  shouldSignOutOnError?: (error: ApolloError) => boolean;
  onError?: (error: ApolloError) => void;
};

const alertOnLogout = (err: ApolloError) => {
  alert(`
      *** ERROR ***
      There was an error fetching the user's data from the database.
      BE error message - ${err.message}
      `);
};

export const useUser = <TData extends { currentUser?: any }, TVariables extends OperationVariables>({
  queryHook,
  isAuthenticated,
  signOut,
  queryOptions,
  shouldSignOutOnError,
  onError,
}: UseUserQueryOptions<TData, TVariables>) => {
  const { data, loading, error } = queryHook({
    ...queryOptions,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
    skip: !isAuthenticated(),
  });

  useEffect(() => {
    if (!error) {
      return;
    }

    const shouldSignOut = shouldSignOutOnError ? shouldSignOutOnError(error) : error.message.includes('Unauthorized');

    if (shouldSignOut) {
      signOut();

      return;
    }

    if (onError) {
      onError(error);

      return;
    }

    alertOnLogout(error);
  }, [error, onError, shouldSignOutOnError, signOut]);

  const user = data?.currentUser;
  const isLoggedIn = !!user;
  const isLoading = loading;

  return { user, isLoggedIn, isLoading, error };
};
